import { useState, useRef, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Plus, StickyNote, LayoutGrid } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { useEventos, useCrearEvento, useEliminarEvento } from '../../hooks/useCalendario';
import { EventoModal } from './components/EventoModal';
import { NotasList } from './components/NotasList';
import { toast } from 'sonner';
import './calendario.css';

type VistaType = 'calendario' | 'notas';

export default function Calendario() {
  const [vista, setVista] = useState<VistaType>('calendario');
  const [fechaInicio, setFechaInicio] = useState<string | null>(null);
  const [fechaFin, setFechaFin] = useState<string | null>(null);
  const [showEventoModal, setShowEventoModal] = useState(false);
  const [eventoSeleccionado, setEventoSeleccionado] = useState<any>(null);
  const calendarRef = useRef<any>(null);

  const { data: eventos = [], refetch } = useEventos(
    fechaInicio || '',
    fechaFin || ''
  );
  const crearEvento = useCrearEvento();
  const eliminarEvento = useEliminarEvento();

  useEffect(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      const view = calendarApi.view;
      const start = view.activeStart.toISOString();
      const end = view.activeEnd.toISOString();
      setFechaInicio(start);
      setFechaFin(end);
    }
  }, []);

  const handleDateChange = (info: any) => {
    setFechaInicio(info.startStr);
    setFechaFin(info.endStr);
  };

  const handleEventClick = (info: any) => {
    const evento = eventos.find(e => e.id === parseInt(info.event.id));
    if (evento) {
      setEventoSeleccionado(evento);
      setShowEventoModal(true);
    }
  };

  const handleDateSelect = () => {
    setEventoSeleccionado(null);
    setShowEventoModal(true);
  };

  const handleGuardarEvento = async (data: any) => {
    try {
      await crearEvento.mutateAsync(data);
      toast.success(eventoSeleccionado ? 'Evento actualizado' : 'Evento creado');
      refetch();
      setShowEventoModal(false);
      setEventoSeleccionado(null);
    } catch {
      toast.error('Error al guardar evento');
    }
  };

  const handleEliminarEvento = async (id: number) => {
    if (!confirm('¿Estás seguro de eliminar este evento?')) return;
    try {
      await eliminarEvento.mutateAsync(id);
      toast.success('Evento eliminado');
      refetch();
      setShowEventoModal(false);
      setEventoSeleccionado(null);
    } catch {
      toast.error('Error al eliminar evento');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Calendario</h1>
          <p className="text-sm text-gray-500">Gestiona eventos, notas y recordatorios</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setVista('calendario')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                vista === 'calendario'
                  ? 'bg-white text-gray-800 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <LayoutGrid size={16} className="inline mr-1" />
              Calendario
            </button>
            <button
              onClick={() => setVista('notas')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                vista === 'notas'
                  ? 'bg-white text-gray-800 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <StickyNote size={16} className="inline mr-1" />
              Notas
            </button>
          </div>
          {vista === 'calendario' && (
            <Button
              onClick={() => {
                setEventoSeleccionado(null);
                setShowEventoModal(true);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus size={16} />
              Nuevo Evento
            </Button>
          )}
        </div>
      </div>

      {vista === 'calendario' ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="calendario-responsive">
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay',
              }}
              initialView="dayGridMonth"
              editable
              selectable
              selectMirror
              dayMaxEvents
              weekends
              events={eventos.map((e: any) => ({
                id: String(e.id),
                title: e.titulo,
                start: e.fechaInicio,
                end: e.fechaFin,
                allDay: e.todoElDia,
                color: e.color,
                textColor: '#ffffff',
              }))}
              eventClick={handleEventClick}
              select={handleDateSelect}
              datesSet={handleDateChange}
              height="auto"
              locale="es"
            />
          </div>
        </div>
      ) : (
        <NotasList />
      )}

      {showEventoModal && (
        <EventoModal
          evento={eventoSeleccionado}
          onClose={() => {
            setShowEventoModal(false);
            setEventoSeleccionado(null);
          }}
          onSave={handleGuardarEvento}
          onDelete={handleEliminarEvento}
        />
      )}
    </div>
  );
}